import { Box, Chip, IconButton, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { ShipToLocationCell } from "@/components/table"
import { AssociationLocationRowActions } from "@/screens/CustomerProducts/components/ProductApprovalPerLocation/AssociationLocationRowActions"
import {
  APPROVAL_STATUS_CHIP_CLASS_MAP,
  APPROVAL_STATUS_LABELS,
  PRIORITY_STATUS_CLASS_MAP,
  PRIORITY_STATUS_LABELS,
} from "@/screens/CustomerProducts/constants"
import type { PreferenceAssociationItem } from "@/screens/CustomerProducts/hooks/useProductPreferenceAssociationList"
import { sortEmptyLast } from "@/utils/table"

interface UseProductLocationAssociationColumnsProps {
  onEditOpen: (row: PreferenceAssociationItem) => void
  onRemoveOpen: (row: PreferenceAssociationItem) => void
}

interface UseProductLocationAssociationColumnsReturn {
  columns: ColumnDef<PreferenceAssociationItem>[]
}

export const useProductLocationAssociationColumns = ({
  onEditOpen,
  onRemoveOpen,
}: UseProductLocationAssociationColumnsProps): UseProductLocationAssociationColumnsReturn => {
  const columns: ColumnDef<PreferenceAssociationItem>[] = [
    {
      accessorKey: "shipToLocation",
      header: "Ship To",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.shipToLocation?.name ?? ""
        const b = rowB.original.shipToLocation?.name ?? ""
        if (!a && !b) return 0
        if (!a) return 1
        if (!b) return -1
        return a.localeCompare(b)
      },
      cell: ({ row }) => <ShipToLocationCell location={row.original.shipToLocation} />,
    },
    {
      accessorKey: "locationCustomerCode",
      header: "Customer Code",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = (rowA.getValue<string[] | null>("locationCustomerCode") ?? []).join(",")
        const b = (rowB.getValue<string[] | null>("locationCustomerCode") ?? []).join(",")
        if (!a && !b) return 0
        if (!a) return 1
        if (!b) return -1
        return a.localeCompare(b)
      },
      cell: ({ row }) => {
        const codes = row.original.locationCustomerCode ?? []
        if (!codes.length) return <Typography variant='body2'>-</Typography>
        return (
          <Box className='flex flex-wrap gap-1'>
            {codes.map((code) => (
              <Chip key={code} size='small' label={code} className='rounded bg-gray-300 text-xs' />
            ))}
          </Box>
        )
      },
    },
    {
      accessorKey: "priorityStatus",
      header: "Priority",
      enableSorting: true,
      sortingFn: (rowA, rowB) => sortEmptyLast(rowA, rowB, "priorityStatus"),
      cell: ({ row }) => {
        const status = row.original.priorityStatus
        if (!status) return <Typography variant='body2'>-</Typography>
        return (
          <Chip
            size='small'
            label={PRIORITY_STATUS_LABELS[status]}
            className={`rounded-[2px] text-xs leading-[18px] ${PRIORITY_STATUS_CLASS_MAP[status]}`}
          />
        )
      },
    },
    {
      accessorKey: "approvalStatus",
      header: "Status",
      enableSorting: true,
      sortingFn: (rowA, rowB) => sortEmptyLast(rowA, rowB, "approvalStatus"),
      cell: ({ row }) => {
        const status = row.original.approvalStatus
        if (!status) return <Typography variant='body2'>-</Typography>
        return (
          <Chip
            size='small'
            variant='outlined'
            label={APPROVAL_STATUS_LABELS[status]}
            className={`text-xs leading-[18px] ${APPROVAL_STATUS_CHIP_CLASS_MAP[status]}`}
          />
        )
      },
    },
    {
      accessorKey: "actions",
      header: "",
      enableSorting: false,
      size: 112,
      cell: ({ row }) => (
        <Box className='flex items-center justify-end gap-x-3'>
          <AssociationLocationRowActions row={row.original} onEditOpen={onEditOpen} onRemoveOpen={onRemoveOpen} />
          {row.getCanExpand() && (
            <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
              {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  return { columns }
}
